/**
 * The TemplatesApi for the web/desktop shell (one per app container): fetch against the API, or in mock mode the
 * in-browser store (code-split, loaded on first use).
 */
import type { TemplatesApi } from "@bundle/core";
import type { Container } from "../container.ts";
import { createTemplatesApi } from "./api.ts";

const byContainer = new WeakMap<Container, TemplatesApi>();

/** An object whose methods wait for `target` (the mock implementation loads lazily). */
function deferred<T extends object>(target: Promise<T>): T {
  return new Proxy({} as T, {
    // Not a thenable: `then` stays undefined so the proxy is never mistaken for a promise.
    get: (_obj, key) => (key === "then" ? undefined : (...args: unknown[]) => target.then((t) => (t[key as keyof T] as (...a: unknown[]) => unknown)(...args))),
  });
}

export function templatesApi(container: Container): TemplatesApi {
  const existing = byContainer.get(container);
  if (existing !== undefined) return existing;
  const api = container.mock
    ? deferred<TemplatesApi>(import("../mock/templatesApi.ts").then(({ createMockTemplatesApi }) => createMockTemplatesApi(() => container.session.state.session?.token)))
    : createTemplatesApi(container.session);
  byContainer.set(container, api);
  return api;
}
